import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { MfaGuard } from "@/components/MfaGuard";
import { CurrencyProvider } from "@/hooks/useCurrency";
import { ComparisonProvider } from "@/contexts/ComparisonContext";
const Index = lazy(() => import("./pages/Index"));

// Lazy load all non-critical pages
const Auth = lazy(() => import("./pages/Auth"));
const MfaEnroll = lazy(() => import("./pages/MfaEnroll"));
const MfaVerify = lazy(() => import("./pages/MfaVerify"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Profile = lazy(() => import("./pages/Profile"));
const MyBookings = lazy(() => import("./pages/MyBookings"));
const Compare = lazy(() => import("./pages/Compare"));
const ActivityDetail = lazy(() => import("./pages/ActivityDetail"));
const BookingConfirmation = lazy(() => import("./pages/BookingConfirmation"));
const PaymentCheckout = lazy(() => import("./pages/PaymentCheckout"));
const PackageDetail = lazy(() => import("./pages/PackageDetail"));
const About = lazy(() => import("./pages/About"));
const Terms = lazy(() => import("./pages/Terms"));
const Wishlist = lazy(() => import("./pages/Wishlist"));
const FAQ = lazy(() => import("./pages/FAQ"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Lazy load admin pages
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout").then(m => ({ default: m.AdminLayout })));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const Activities = lazy(() => import("./pages/admin/Activities"));
const ActivityForm = lazy(() => import("./pages/admin/ActivityForm"));
const Bookings = lazy(() => import("./pages/admin/Bookings"));
const Categories = lazy(() => import("./pages/admin/Categories"));
const Reviews = lazy(() => import("./pages/admin/Reviews"));
const PromoCodes = lazy(() => import("./pages/admin/PromoCodes"));
const Packages = lazy(() => import("./pages/admin/Packages"));
const PackageForm = lazy(() => import("./pages/admin/PackageForm"));
const Utilities = lazy(() => import("./pages/admin/Utilities"));
const Users = lazy(() => import("./pages/admin/Users"));
const TodaysExcursions = lazy(() => import("./pages/admin/TodaysExcursions"));
const PickupZones = lazy(() => import("./pages/admin/PickupZones"));
const Fleet = lazy(() => import("./pages/admin/Fleet"));
const PartnerAgencies = lazy(() => import("./pages/admin/PartnerAgencies"));

// Lazy load rep pages
const RepDashboard = lazy(() => import("./pages/rep/RepDashboard"));
const RepBookingForm = lazy(() => import("./pages/rep/RepBookingForm"));

// Lazy load guide pages
const GuideDashboard = lazy(() => import("./pages/guide/GuideDashboard"));
const GuideManifest = lazy(() => import("./pages/guide/GuideManifest"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 1,
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const App = () => (
  <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <QueryClientProvider client={queryClient}>
      <CurrencyProvider>
        <ComparisonProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
          <BrowserRouter>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/mfa/enroll" element={<MfaEnroll />} />
                <Route path="/mfa/verify" element={<MfaVerify />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/" element={<Index />} />
                <Route path="/activities/:slug" element={<ActivityDetail />} />
                <Route path="/booking/confirmation/:reference" element={<BookingConfirmation />} />
                <Route path="/payment/checkout/:reference" element={<PaymentCheckout />} />
                <Route path="/compare" element={<Compare />} />
                <Route path="/packages/:slug" element={<PackageDetail />} />
                <Route path="/about" element={<About />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/faq" element={<FAQ />} />
                <Route path="/wishlist" element={
                  <ProtectedRoute>
                    <Wishlist />
                  </ProtectedRoute>
                } />
                <Route path="/dashboard" element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } />
                <Route path="/my-bookings" element={
                  <ProtectedRoute>
                    <MyBookings />
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <Profile />
                  </ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <AdminDashboard />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/activities" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <Activities />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/activities/new" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <ActivityForm />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/activities/:id" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <ActivityForm />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/bookings" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <Bookings />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/categories" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <Categories />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/reviews" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <Reviews />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/promo-codes" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <PromoCodes />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/packages" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <Packages />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/packages/new" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <PackageForm />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/packages/:id" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <PackageForm />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/users" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <Users />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/todays-excursions" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <TodaysExcursions />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/zones" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <PickupZones />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/fleet" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <Fleet />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/utilities" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <Utilities />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                <Route path="/admin/partner-agencies" element={
                  <ProtectedRoute requireAdmin>
                    <AdminLayout>
                      <PartnerAgencies />
                    </AdminLayout>
                  </ProtectedRoute>
                } />
                {/* Rep routes */}
                <Route path="/rep" element={
                  <ProtectedRoute requireRep>
                    <RepDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/rep/book/:activityId" element={
                  <ProtectedRoute requireRep>
                    <RepBookingForm />
                  </ProtectedRoute>
                } />
                {/* Guide routes */}
                <Route path="/guide" element={
                  <ProtectedRoute requireGuide>
                    <GuideDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/guide/manifest/:activityId/:date" element={
                  <ProtectedRoute requireGuide>
                    <GuideManifest />
                  </ProtectedRoute>
                } />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          </TooltipProvider>
        </ComparisonProvider>
      </CurrencyProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
